'use client'

import { useEffect, useState } from 'react'

import { useOverlayState } from '@heroui/react'

import { ErrorCard } from '@/components/generic-error-component'
import { MediaGallery } from '@/components/media'
import { ProjectMediaCarousel } from '@/components/media/core/ProjectMediaCarousel'
import { Bone } from '@/components/ui/bone'
import { Chip } from '@/components/ui/chip'
import { Icon } from '@/components/ui/icon'
import { COMMERCIAL_KEYWORD } from '@/components/v2/portfolio/portfolio-types'
import { V2PageShell } from '@/components/v2/v2-page-shell'
import { useAppDispatch } from '@/lib/store'
import { setSelectedProject } from '@/lib/store/features/projects'
import { ProjectItem } from '@/lib/types/content/project'

interface GalleryClientProps {
  project: ProjectItem
  className?: string
}

export function Client({
  project,
  className = ''
}: GalleryClientProps) {
  const dispatch = useAppDispatch()
  const { isOpen, open, close } = useOverlayState()
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (project?.id && project?.slug) {
      dispatch(
        setSelectedProject({
          ...project,
          isSelected: true
        })
      )
    }
  }, [dispatch, project])

  if (!project?.id || !project?.slug) {
    return <ErrorCard message="We couldn't load this project's information." />
  }

  const handleMediaClick = (index: number) => {
    setCurrentIndex(index)
    open()
  }

  const hasMedia = project.media && project.media.length > 0
  const mediaCount = project.media?.length ?? 0

  // Commercial is carried as a keyword rather than its own column (see
  // portfolio-types), so the detail page reads the same field the cards do.
  const keywords = project.keywords ?? []
  const isCommercial = keywords.includes(COMMERCIAL_KEYWORD)
  // The chip next to the category would repeat it for categories that already
  // mean commercial; the facts panel still states the work type in full.
  const showCommercial =
    isCommercial && !['commercial-graphics', 'fleet-wraps'].includes(project.category?.slug ?? '')
  const tags = keywords
    .filter((k) => k !== COMMERCIAL_KEYWORD)
    .map((k) => k.charAt(0).toUpperCase() + k.slice(1))
  const projectDate = project.publishedAt ?? project.createdAt
  const year = projectDate ? new Date(projectDate).getUTCFullYear() : null

  return (
    <V2PageShell>
      <div className={`mx-auto max-w-[1200px] px-6 py-10 ${className}`}>
        {/* Media Gallery */}
        <div className="mb-8">
          {hasMedia ? (
            <>
              <MediaGallery
                media={project.media}
                isOpen={isOpen}
                onClose={close}
                currentIndex={currentIndex}
                onIndexChange={setCurrentIndex}
              />
              <ProjectMediaCarousel
                media={project.media}
                currentIndex={currentIndex}
                onIndexChange={setCurrentIndex}
                onOpenLightbox={handleMediaClick}
              />
            </>
          ) : (
            <div className="relative flex aspect-video w-full items-center justify-center rounded-2xl bg-surface-secondary">
              <Icon icon="image" className="h-20 w-20 text-muted" />
            </div>
          )}
        </div>

        {/* Project Details */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          <div className="min-w-0 md:col-span-2">
            <div className="mb-8">
              <h1 className="cw-display-2 mb-4 [overflow-wrap:anywhere]">{project.title}</h1>
              {(project.category || showCommercial) && (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  {project.category && (
                    <Chip data-testid="project-category-chip" className="bg-accent text-accent-foreground">
                      {project.category.name}
                    </Chip>
                  )}
                  {showCommercial && (
                    <Chip data-testid="project-commercial-chip" className="bg-accent-foreground text-background">
                      Commercial
                    </Chip>
                  )}
                </div>
              )}
            </div>
            <div className="prose prose-lg max-w-none">
              {project.description &&
                project.description
                  .split(/\n{2,}/)
                  .map((para, i) => <p key={i}>{para}</p>)}
            </div>
          </div>

          {/* Job facts. Lives in the third column, which the two-column body
              left empty, and stacks under the description on mobile. */}
          <aside className="min-w-0">
            <div className="rounded-2xl border border-border bg-surface p-5">
              <p className="cw-eyebrow mb-4">Project details</p>
              <dl className="space-y-3 text-sm">
                {project.category && (
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-muted">Category</dt>
                    <dd className="text-right text-foreground">{project.category.name}</dd>
                  </div>
                )}
                {isCommercial && (
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-muted">Work type</dt>
                    <dd className="text-right text-foreground">Commercial</dd>
                  </div>
                )}
                {year && (
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-muted">Year</dt>
                    <dd className="text-right text-foreground">{year}</dd>
                  </div>
                )}
                {mediaCount > 0 && (
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-muted">Photos</dt>
                    <dd className="text-right text-foreground">{mediaCount}</dd>
                  </div>
                )}
              </dl>

              {tags.length > 0 && (
                <div className="mt-5 border-t border-border pt-5">
                  <p className="cw-eyebrow mb-3">What this job involved</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <Chip key={tag} className="bg-surface-secondary text-foreground">
                        {tag}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </V2PageShell>
  )
}

Client.Skeleton = function GalleryClientSkeleton() {
  return (
    <V2PageShell>
      <div className="mx-auto max-w-[1200px] px-6 py-10">
        {/* Media Gallery */}
        <div className="mb-8 space-y-3">
          <Bone className="aspect-video w-full rounded-2xl" />
          <div className="flex gap-2 overflow-x-auto pb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Bone key={i} className="aspect-square w-20 shrink-0 rounded-md" />
            ))}
          </div>
        </div>

        {/* Project Details */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          <div className="min-w-0 md:col-span-2">
            {/* Title (cw-display-2) + category chip */}
            <div className="mb-8 space-y-4">
              <Bone className="h-9 w-3/4" />
              <Bone className="h-6 w-24 rounded-full" />
            </div>
            {/* Description paragraphs */}
            <div className="space-y-3">
              <Bone className="h-4 w-full" />
              <Bone className="h-4 w-full" />
              <Bone className="h-4 w-5/6" />
              <Bone className="h-4 w-full" />
              <Bone className="h-4 w-2/3" />
            </div>
          </div>

          {/* Project details panel */}
          <div className="rounded-2xl border border-border bg-surface p-5">
            <Bone className="h-3 w-28" />
            <div className="mt-4 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Bone key={i} className="h-4 w-full" />
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Bone key={i} className="h-6 w-20 rounded-full" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </V2PageShell>
  )
}
